// OrderList.tsx
import React from "react";
import { OrderListProps } from "./types";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import { FileText, FlaskConical } from "lucide-react";
import { OrderItemSkeleton } from "../StockOrderSkeleton";

const OrderList: React.FC<OrderListProps> = ({
  orders,
  type,
  onOrderClick,
  selectedOrder,
  activeTab,
  withPrescription,
  orderStatus,
  isLoading,
}) => {
   if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <OrderItemSkeleton key={i} />
        ))}
      </div>
    );
  }
  if (orders.length === 0) {
    return (
      <div className="p-6 border border-dashed border-gray-200 rounded-lg text-center text-gray-500 flex flex-col items-center gap-2">
        {activeTab === "medicine" ? (
          <FileText className="h-10 w-10 text-gray-400" />
        ) : (
          <FlaskConical className="h-10 w-10 text-gray-400" />
        )}
        <p className="text-lg font-medium">
          {activeTab === "medicine"
            ? withPrescription
              ? "No orders with prescription found"
              : "No orders without prescription found"
            : `No ${getLabStatusText(orderStatus)} orders found`}
        </p>
        <p className="text-sm text-gray-400">
          {activeTab === "medicine"
            ? withPrescription
              ? "When new prescription orders arrive, they'll appear here"
              : "When new non-prescription orders arrive, they'll appear here"
            : "When new lab orders matching your filter arrive, they'll appear here"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {orders.map((order) => {
        const isActive = selectedOrder?.id === order.id;

        return (
          <div
            key={order.id}
            onClick={() => onOrderClick(order)}
            className={`border rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer ${
              isActive
                ? "bg-gray-200 border-green-300 ring-2 ring-green-200"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`flex items-center justify-between ${
                activeTab === "lab" ? "w-[24vw]" : "w-auto"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    type === "medicine" ? "bg-green-100" : "bg-blue-100"
                  }`}
                >
                  <img
                    src={medicineIcon}
                    alt="not-found"
                    className={`w-8 h-7 ${
                      type === "medicine" ? "text-green-600" : "text-blue-600"
                    }`}
                  />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {order.orderNumber} {/* Show order number instead */}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {order.patientDetails?.patientName || order.details}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500 mb-1">{order.date}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function getLabStatusText(status: "new" | "awaiting" | "approved"): string {
  switch (status) {
    case "new":
      return "new";
    case "awaiting":
      return "awaiting results";
    case "approved":
      return "approved";
    default:
      return "";
  }
}

export default OrderList;
